
import { computed, defineComponent, ref, watch } from "vue";
import { ErrorMessage, Field, Form } from "vee-validate";
import * as Yup from "yup";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import axios from "axios";
import Swal from "sweetalert2/dist/sweetalert2.js";
import config from "devextreme/core/config";
import { formatDate } from "devextreme/localization";
import JwtService from "@/core/services/JwtService";

interface DonorDetails {
  name: string;
  mobileNo: string;
  member_id: string;
  email: string;
  amount: number;
  mode: string;
  remarks: string;
  communications: {
    email: boolean;
    phone: boolean;
    whatsapp: boolean;
    address: string;
  };
}
interface ReceiptDetails {
  book_no: number;
  receipt_no: number;
  kcm_receipt_no: number;
  created_at: string;
  collector_id: number;
  uid_no: number;
  qr_id: string;
  whatsapp: string;
  sms: string;
  amount: number;
  lastModified: string;
  payment_id: string;
}

let intervalTimer;
let paymentTimer;

function zeroPadded(num) {
  // 4 --> 04
  return num < 10 ? `0${num}` : num;
}
function hourConvert(hour) {
  // 15 --> 3
  return hour % 12 || 12;
}

const Toast = Swal.mixin({
  toast: true,
  position: "top-end",
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
});

export default defineComponent({
  name: "gateway-general",
  props: {
    amount: { type: Number, required: false },
    settings: {
      type: Object,
      // eslint-disable-next-line vue/require-valid-default-prop
      default: {
        collector_id: null,
        source: "",
        displayPaymentMode: "true",
        fetchMemberDetails: "false",
        fixedAmount: "false",
      },
      required: false,
    },
  },
  components: {
    ErrorMessage,
    Field,
    // eslint-disable-next-line vue/no-reserved-component-names
    Form,
  },
  setup(props) {
    const store = useStore();
    const router = useRouter();
    const users = computed(() => {
      return store.getters.currentUser;
    });
    const donationDetails = computed(() => {
      return props.amount
        ? { scheme_Amount: props.amount }
        : store.getters.currentDonationDetails;
    });
    const propsSettings = computed(() => {
      return props.settings;
    });
    const donorDetails = ref<DonorDetails>({
      name: "",
      mobileNo: "",
      member_id: "",
      email: "",
      amount: donationDetails.value ? donationDetails.value.scheme_Amount : 0,
      mode: propsSettings.value.displayPaymentMode === "true" ? "QR" : "UPI",
      remarks: "",
      communications: {
        email: false,
        phone: false,
        whatsapp: false,
        address: "",
      },
    });
    const isPaused = ref(false);
    config({ defaultCurrency: "INR" });
    const isLoaded = ref(false);
    const overlaySwitch = computed(() => {
      return {
        active: true,
        "overlay overlay-block": !isLoaded.value,
      };
    });
    const paymentButton = ref<HTMLElement | null>(null);
    const paymentSchema = Yup.object().shape({
      name: Yup.string().required().uppercase().label("Donor Name"),
      mobileNo: Yup.string().required().max(10).min(10).label("Mobile Number"),
      email: Yup.string().required().email("Not a proper email"),
    });
    const timeLeft = ref("00:00") as any;
    const endTime = ref("0") as any;
    const qrReady = ref(null) as any;
    const selectedTime = ref(0);
    const isNewUid = ref(false);
    const currentQR_id = ref(null) as any;
    const paymentAcknowledgment = ref<ReceiptDetails>({
      book_no: 0,
      receipt_no: 0,
      kcm_receipt_no: 0,
      created_at: "",
      collector_id: 0,
      uid_no: 0,
      qr_id: "",
      whatsapp: "",
      sms: "",
      amount: 0,
      lastModified: "",
      payment_id: "",
    });

    const onImgLoad = () => {
      isLoaded.value = true;
    };

    const retriveQRCode = async (values) => {
      // eslint-disable-next-line no-empty
      if (paymentButton.value) {
        // Activate indicator
        paymentButton.value.setAttribute("data-kt-indicator", "on");

        qrReady.value = null;
        currentQR_id.value = null;
        resetPaymentAck();

        try {
          //creating new icm id for a new donor
          if (isNewUid.value) {
            await createNewContact();
          }

          // Customer details and amount
          const data = {
            name: donationDetails.value.name || "General Donation",
            payment_amount: donorDetails.value.amount * 100,
            description: `for ${
              donationDetails.value.name || "General Donation"
            } of Rs.${donorDetails.value.amount}`,
            notes: {
              icm_id: donorDetails.value.member_id,
              first_Name: donorDetails.value.name,
              Mobile1: donorDetails.value.mobileNo,
              scheme_id: donationDetails.value.scheme_id,
              scheme_Name: donationDetails.value.name,
              scheme_Amount: donationDetails.value.scheme_Amount || 0,
              scheme_Type: "KCP",
              collector_id: users.value.uid,
              collector_Name: users.value.name,
              receipt_Date: formatDate(new Date(), "dd-MM-yyyy"),
            },
          };

          await axios.post("/payment/qr_codes", data).then((response) => {
            const { id, image_url } = response.data;
            qrReady.value = image_url;
            currentQR_id.value = id;
            setTime(180);
          });
        } catch (e) {
          qrReady.value = null;
          currentQR_id.value = null;
        } finally {
          paymentButton.value?.removeAttribute("data-kt-indicator");
        }
      }
    };

    const makePayment = async (values) => {
      // eslint-disable-next-line no-empty
      if (donorDetails.value.amount < 100) {
        Swal.fire({
          text: "Minimum donation amount is Rs.100",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Try again!",
          customClass: {
            confirmButton: "btn fw-semobold btn-light-danger",
          },
        });
        return;
      }

      if (
        donorDetails.value.communications.whatsapp &&
        donorDetails.value.communications.address.length < 10
      ) {
        Swal.fire({
          text: "Address quality is very low.",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Try again!",
          customClass: {
            confirmButton: "btn fw-semobold btn-light-danger",
          },
        });
        return;
      }
      if (paymentButton.value) {
        // Activate indicator
        paymentButton.value.setAttribute("data-kt-indicator", "on");

        //creating new icm id for a new donor
        if (isNewUid.value) {
          await createNewContact();
        }

        // Customer details and amount
        const data = {
          amount: donorDetails.value.amount * 100,
          name: donorDetails.value.name,
          email: donorDetails.value.email,
          phone: donorDetails.value.mobileNo,
          notes: {
            mode:
              propsSettings.value.source !== ""
                ? propsSettings.value.source
                : "Mobile App",
          },
        };

        await axios
          .post("/payment/createOrder", data)
          .then((response) => {
            const order: any = response.data;

            const options = {
              key: process.env.RAZORPAY_KEY,
              amount: order.amount_due,
              currency: order.currency,
              name: "ISKCON CBE",
              description: `for ${
                donationDetails.value.name || "General Donation"
              } of Rs.${donorDetails.value.amount}`,
              order_id: order.id,
              prefill: {
                name: data.name,
                email: data.email,
                contact: data.phone,
              },
              notes: {
                icm_id: donorDetails.value.member_id,
                first_Name: donorDetails.value.name,
                Mobile1: donorDetails.value.mobileNo,
                scheme_id: donationDetails.value.scheme_id,
                scheme_Name: donationDetails.value.name,
                scheme_Amount: donationDetails.value.scheme_Amount || 0,
                collector_id: users.value.uid,
                collector_Name: users.value.name,
                mode: data.notes.mode,
                receipt_Date: formatDate(new Date(), "dd-MM-yyyy"),
              },
              theme: {
                color: "#528FF0", // Set your website theme color
              },
              handler: async (response) => {
                await verifySignature(response);
              },
            };

            // eslint-disable-next-line no-undef
            const _window = window as any;
            const rzp = new _window.Razorpay(options);
            rzp.on("payment.failed", function (response) {
              const error = `Error Code: ${response.error.code}; Desc: ${response.error.description};
              Reason: ${response.error.reason}; Order_ID: ${response.error.metadata.order_id};
              Payment_ID: ${response.error.metadata.payment_id}`;

              Swal.fire({
                text: error,
                icon: "error",
                buttonsStyling: false,
                confirmButtonText: "Try again!",
                customClass: {
                  confirmButton: "btn fw-semobold btn-light-danger",
                },
              });
            });
            rzp.open();
          })
          .catch((error) => {
            console.error("Error:", error);
          });

        paymentButton.value?.removeAttribute("data-kt-indicator");
      }
    };

    const verifySignature = async (response) => {
      await axios
        .post("/payment/verifyPayment", response)
        .then((resp) => {
          //sms, whatsapp receipts
          sendAcknowledgements(response);

          //if webUser
          if (propsSettings.value.source !== "") {
            Swal.fire({
              title: "Payment Received and Verified by Razorpay.!",
              html: `<span class="text-dark fw-bold">Payment_ID:</span> ${response.razorpay_payment_id};
                    <span class="text-dark fw-bold">Order_ID:</span> ${response.razorpay_order_id};
                   `,
              icon: "success",
              showCancelButton: true,
              confirmButtonColor: "#3085d6",
              cancelButtonColor: "#d33",
              confirmButtonText: "Done.",
            });
          } else {
            Swal.fire({
              title: "Payment Received and Verified by Razorpay.!",
              html: `<span class="text-dark fw-bold">Payment_ID:</span> ${response.razorpay_payment_id};
                    <span class="text-dark fw-bold">Order_ID:</span> ${response.razorpay_order_id};
                   `,
              icon: "success",
              showCancelButton: true,
              confirmButtonColor: "#3085d6",
              cancelButtonColor: "#d33",
              confirmButtonText: "Go to Home",
              cancelButtonText: "New Payment",
            }).then((result) => {
              if (result.isConfirmed) {
                router.push({ name: "dashboard" });
              }
            });
          }
        })
        .catch((err) => {
          Swal.fire({
            text: "Payment Verification Error.",
            icon: "error",
            html: `<span class="text-dark fw-bold">Payment_ID:</span> ${response.razorpay_payment_id};
                    <span class="text-dark fw-bold">Order_ID:</span> ${response.razorpay_order_id};`,
            buttonsStyling: false,
            confirmButtonText: "Ok",
            customClass: {
              confirmButton: "btn fw-semobold btn-light-danger",
            },
          });
          resetDonorDetails();
        });
    };

    const sendAcknowledgements = async (response) => {
      //sms receipt
      const smsData = {
        refNo: 0,
        mobile: donorDetails.value.mobileNo,
        msgTxt: `Hare Krishna | Dear ${donorDetails.value.name
          .replace(/[#&.*+?^${}()|[\]\\]/g, " ")
          .trim()},Thank you very much for your contribution of Rs.${
          donorDetails.value.amount
        }. For Enquires: 8438416108 - ISKCON Coimbatore.`,
      };
      await axios.post("https://api.biolinelab.com/kcmsms", smsData);

      //eReceipt
      const eReceiptData = {
        address1: donorDetails.value.communications.address,
        address2: "",
        city: "",
        pin: "",
        email: donorDetails.value.email,
        pan: "",
        receipt_no: 0,
        created_at: formatDate(new Date(), "dd-MM-yyyy"),
        collector_id: users.value.uid || 0,
        uid_no: donorDetails.value.member_id,
        amount: donorDetails.value.amount,
        name: donorDetails.value.name,
        mobile: donorDetails.value.mobileNo,
      };

      await axios
        .get(`/payment/receipt_byPaymentId/${response.razorpay_payment_id}`)
        .then(({ data }) => {
          eReceiptData.receipt_no = data.receipt_no;
        })
        .catch((err) => err);

      await axios
        .post("https://api.biolinelab.com:9710/makeEReceipt", eReceiptData)
        .then(async (data: any) => {
          await axios.post("https://api.biolinelab.com:9710/sendEReceipt", {
            name: eReceiptData.name,
            amount: eReceiptData.amount,
            mobile: eReceiptData.mobile,
            receipt_no: eReceiptData.receipt_no,
          });
          // if (data.message === "Generated Successfully.") {
          //
          // }
        })
        .catch((err) => {
          Swal.fire({
            text: `eReceipt Couldn't Generated. ${err}`,
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Ok",
            customClass: {
              confirmButton: "btn fw-semobold btn-light-danger",
            },
          });
        });

      resetDonorDetails();
    };

    const verifyPayment = async () => {
      // Swal.showLoading();
      // Swal.update({ allowOutsideClick: false, showConfirmButton: false });
      // const b = Swal.getHtmlContainer();
      // b.style = ""; //to remove display:none which is default..
      // b.textContent = "Don't Refresh this page..Payment is being processed..";

      //cleaning QR Timer..
      endTime.value = 0;
      qrReady.value = null;
      clearInterval(intervalTimer);

      //kcm_receipt_no found
      if (paymentAcknowledgment.value.kcm_receipt_no) {
        clearInterval(paymentTimer);
        // b.textContent = "";
        // Swal.close();

        await sendAcknowledgements({
          razorpay_payment_id: paymentAcknowledgment.value.payment_id,
        });

        Swal.fire({
          title: "Payment Received and Verified by Razorpay.!",
          html: `<span class="text-dark fw-bold">Payment_ID:</span> ${paymentAcknowledgment.value.payment_id};
                    <span class="text-dark fw-bold">Receipt No:</span> ${paymentAcknowledgment.value.receipt_no};
                    <span class="text-dark fw-bold">KCM Ref.No:</span> ${paymentAcknowledgment.value.kcm_receipt_no};
                   `,
          icon: "success",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Go to Home",
          cancelButtonText: "New Payment",
        }).then((result) => {
          if (result.isConfirmed) {
            router.push({ name: "dashboard" });
          } else if (result.isDismissed) {
            resetDonorDetails();
          }
        });

        isPaused.value = false;
        resetPaymentAck();
      }
    };

    const checkMode = (mode) => {
      donorDetails.value.mode = mode;
    };

    const setTime = (seconds) => {
      timer(seconds);
    };

    const timer = (seconds) => {
      const now = Date.now();
      const end = now + seconds * 1000;
      displayTimeLeft(seconds);

      selectedTime.value = seconds;
      // this.initialTime = seconds;
      displayEndTime(end);
      countdown(end);
    };

    const countdown = (end) => {
      // this.initialTime = this.selectedTime;
      intervalTimer = setInterval(() => {
        const secondsLeft = Math.round((end - Date.now()) / 1000);

        if (secondsLeft === 0) {
          endTime.value = 0;
          qrReady.value = null;
          Swal.fire({
            text: "Sorry, QR Code expired! Please try again.",
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn btn-primary",
            },
          });
        }

        if (secondsLeft < 0) {
          clearInterval(intervalTimer);
          return;
        }

        displayTimeLeft(secondsLeft);
      }, 1000);

      //fetching payment state..
      paymentTimer = setInterval(() => {
        const secondsLeft = Math.round((end - Date.now()) / 1000);
        if (secondsLeft === 0) {
          Swal.fire({
            text: `Sorry, Payment details couldn't be fetched! Ref#: ${paymentAcknowledgment.value.payment_id}`,
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn btn-primary",
            },
          });
        }
        if (secondsLeft < 0) {
          qrReady.value = null;
          isPaused.value = false;
          Swal.close();
          resetPaymentAck();
          clearInterval(paymentTimer);
          return;
        }

        try {
          axios
            .get(`/payment/receipt_byEntityId/${currentQR_id.value}`)
            .then(({ data }) => {
              if (data.entity_id === currentQR_id.value) {
                isPaused.value = true;
                paymentAcknowledgment.value = data;
              }
            });
        } catch (error) {
          return;
        }
      }, 7000);
    };

    watch([isPaused], (newVal, oldValue) => {
      if (newVal) {
        verifyPayment();
      }
    });

    const displayTimeLeft = (secondsLeft) => {
      const minutes = Math.floor((secondsLeft % 3600) / 60);
      const seconds = secondsLeft % 60;

      timeLeft.value = `${zeroPadded(minutes)}:${zeroPadded(seconds)}`;
    };

    const displayEndTime = (timestamp) => {
      const end = new Date(timestamp);
      const hour = end.getHours();
      const minutes = end.getMinutes();
      const ampm = hour > 12 ? "pm" : "am";

      endTime.value = `${hourConvert(hour)}:${zeroPadded(minutes)} ${ampm}`;
    };

    const onInputChange = (e) => {
      const regex = /^[a-zA-Z\s]*$/;
      if (!regex.test(e.target.value)) {
        donorDetails.value.name = "";
        Toast.fire({
          icon: "error",
          title: "Invalid Donor Name",
        });
      }
    };

    const resetDonorDetails = () => {
      donorDetails.value = {
        name: "",
        mobileNo: "",
        member_id: "",
        email: "",
        amount: 0,
        mode: propsSettings.value.displayPaymentMode === "true" ? "QR" : "UPI",
        remarks: "",
        communications: {
          email: false,
          phone: false,
          whatsapp: true,
          address: "",
        },
      };
    };

    const resetPaymentAck = () => {
      paymentAcknowledgment.value = {
        book_no: 0,
        receipt_no: 0,
        kcm_receipt_no: 0,
        created_at: "",
        collector_id: 0,
        uid_no: 0,
        qr_id: "",
        whatsapp: "",
        sms: "",
        amount: 0,
        lastModified: "",
        payment_id: "",
      };
    };

    const getMembersByMobile1 = async (e) => {
      resetDonorDetails();
      donorDetails.value.amount = donationDetails.value
        ? donationDetails.value.scheme_Amount
        : 0;
      donorDetails.value.mobileNo = e.target.value;
      isNewUid.value = false;
      try {
        if (
          e.target.value.length === 10 &&
          ["6", "7", "8", "9"].some((item) => e.target.value.startsWith(item))
        ) {
          Swal.showLoading();
          Swal.update({ allowOutsideClick: false, showConfirmButton: false });
          const b = Swal.getHtmlContainer();
          b.style = ""; //to remove display:none which is default..
          b.textContent =
            propsSettings.value.fetchMemberDetails === "true"
              ? "Fetching member data.."
              : "Preparing..";

          const filterData = [
            {
              field: "phone",
              searchVal: donorDetails.value.mobileNo.toString(),
            },
            { sort: { member_id: -1 }, limit: 2, labels: "" },
            { labels: "" },
          ];

          await fetch(`${process.env.VUE_APP_API_URL}/contacts/getContacts`, {
            method: "POST",
            body: JSON.stringify(filterData),
            headers: {
              "Content-Type": "application/json",
              "x-access-token": `${JwtService.getToken()}`,
            },
          })
            .then((response) => response.json())
            .then(({ data }) => {
              if (data.length > 0) {
                b.textContent = "Member Data already exists..";
                isNewUid.value = false;
                donorDetails.value.member_id = data[0].member_id;
                donorDetails.value.name = data[0].first_Name;
                donorDetails.value.email =
                  propsSettings.value.fetchMemberDetails === "true"
                    ? data[0].emails[0]
                    : "";
                donorDetails.value.communications.address =
                  propsSettings.value.fetchMemberDetails === "true"
                    ? data[0].addresses[0].address
                    : "";
              } else {
                b.textContent = "This is a New Contact..";
                isNewUid.value = true;
              }
            });

          Swal.close();
        } else {
          donorDetails.value.mobileNo = "";
          Swal.fire({
            icon: "warning",
            title: "Oops...Invalid Mobile No!",
            text: `Is it ${e.target.value}?`,
            showCancelButton: true,
            showConfirmButton: false,
            cancelButtonColor: "#d33",
            cancelButtonText: "Ok, I'll fix!",
          });
          const myTitle = document.getElementById(
            "swal2-title"
          ) as HTMLDivElement | null;
          if (myTitle != null) {
            myTitle.style.display = "unset";
          }
        }
      } catch (err) {
        donorDetails.value.mobileNo = "";
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Lost Server Connectivity!",
          footer: `<p>${err}</p>`,
          confirmButtonColor: "#a40000",
        });
      }
    };

    const createNewContact = async () => {
      try {
        donorDetails.value.member_id = "";
        const newDonorDetails = {
          member_id: 1,
          first_Name: donorDetails.value.name,
          emails: [donorDetails.value.email],
          alias: donorDetails.value.mobileNo.toString(),
          phone_nos: [
            {
              phone_no: donorDetails.value.mobileNo.toString(),
              type: "mobile",
              tag: ["Primary"],
              verified: false,
            },
          ],
          addresses: [
            {
              type: "Billing Address",
              address: donorDetails.value.communications.address,
              verified: false,
            },
          ],
          member_Type: "Friends",
          contact_src: "mobileApp",
          logs: ["Created at: " + new Date().toLocaleString()],
          schema_version: 3,
        };

        await fetch(`${process.env.VUE_APP_API_URL}/contacts/createContact`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": `${JwtService.getToken()}`,
          },
          body: JSON.stringify(newDonorDetails),
        })
          .then((response) => response.json())
          .then((data) => {
            donorDetails.value.member_id = data.member_id;
          });

        return true;
      } catch (e) {
        return false;
      }
    };

    return {
      donorDetails,
      makePayment,
      retriveQRCode,
      qrReady,
      paymentSchema,
      users,
      paymentButton,
      checkMode,
      setTime,
      timeLeft,
      endTime,
      isLoaded,
      onImgLoad,
      overlaySwitch,
      onInputChange,
      getMembersByMobile1,
      isPaused,
      propsSettings,
    };
  },
});
